import { ShareTo } from "../../config/Enums";
import { EventBus } from "../../helpers/EventBus";
import { IApiResponse, IShareSocialMediaItem, ITwitterPost } from "../../models/Interfaces";
import { AjaxService } from "../AjaxService";
import PostFormatter from "../socialmedia/PostFormatter";
import { ISocialPublishResult, ISocialPublishingProvider } from "./types";

enum PublishingTwitterStorage {
  refreshToken = "PublishingTwitterRefreshToken",
  accessToken = "PublishingTwitterAccessToken",
  oauth1AccessToken = "PublishingTwitterOAuth1AccessToken",
  oauth1AccessSecret = "PublishingTwitterOAuth1AccessSecret",
  oauth1UserId = "PublishingTwitterOAuth1UserId",
  oauth1ScreenName = "PublishingTwitterOAuth1ScreenName",
  codeVerifier = "PublishingTwitterCodeVerifier",
  oauth1Mode = "PublishingTwitterOAuth1Mode",
  flowMode = "PublishingTwitterFlowMode",
  requiresOAuth1 = "PublishingTwitterRequiresOAuth1",
  freshAuthReady = "PublishingTwitterFreshAuthReady"
}

type TwitterPublishingExchangeResult = {
  success: boolean;
  accessToken?: string | null;
  accessTokenSecret?: string | null;
  error: string | null;
};

class TwitterPublishingProvider implements ISocialPublishingProvider {
  public readonly platform = ShareTo.X;
  private readonly ajaxService = new AjaxService();
  private readonly postingScopes = "tweet.read%20tweet.write%20users.read%20offline.access";
  private readonly intentUrl = "https://twitter.com/intent/tweet";

  public isEnabled(): boolean {
    if (this.getOAuthVersion() === "1") {
      return !!(this.getOAuth1RequestTokenEndpoint() && this.getOAuth1ExchangeEndpoint() && this.getOAuth1RedirectUri());
    }

    return !!(this.getOAuth2ClientId() && this.getOAuth2AuthUrl() && this.getOAuth2RedirectUri());
  }

  public isConnected(): boolean {
    if (this.getOAuthVersion() === "1") {
      return this.checkAuth1State();
    }

    return this.checkAuth1State() && this.checkAuth2State();
  }

  public static isAwaitingPublishingCallback(): boolean {
    return window.localStorage.getItem(PublishingTwitterStorage.flowMode) === "publishing";
  }

  private getOAuthVersion(): "1" | "2" {
    return import.meta.env.VITE_TWITTER_PUBLISHING_OA_VER === "1" ? "1" : "2";
  }

  private getOAuth2ClientId(): string | undefined {
    return import.meta.env.VITE_TWITTER_OA2_CLIENT_ID;
  }

  private getOAuth2AuthUrl(): string | undefined {
    return import.meta.env.VITE_TWITTER_OA2_AUTH_URL;
  }

  private getOAuth2RedirectUri(): string {
    return import.meta.env.VITE_TWITTER_OA2_REDIRECT_URI ?? `${window.location.origin}/TwitterCallback`;
  }

  private getOAuth1RedirectUri(): string {
    return import.meta.env.VITE_TWITTER_OA1_REDIRECT_URI ?? `${window.location.origin}/TwitterCallback`;
  }

  private getOAuth1RequestTokenEndpoint(): string {
    return import.meta.env.VITE_TWITTER_PUBLISHING_OA1_REQUEST_TOKEN_ENDPOINT ?? "/SocialPublishing/GetTwitterOAuth1RequestToken";
  }

  private getOAuth1ExchangeEndpoint(): string {
    return import.meta.env.VITE_TWITTER_PUBLISHING_OA1_EXCHANGE_TOKEN_ENDPOINT ?? "/SocialPublishing/TwitterExchangeOAuth1";
  }

  private getOAuth2ExchangeEndpoint(): string {
    return import.meta.env.VITE_TWITTER_PUBLISHING_OA2_EXCHANGE_TOKEN_ENDPOINT ?? "/SocialPublishing/TwitterExchangeOAuth2";
  }

  private getPostEndpoint(): string {
    return this.getOAuthVersion() === "1"
      ? (import.meta.env.VITE_TWITTER_PUBLISHING_OA1_POST_ENDPOINT ?? "/SocialPublishing/TwitterPostOAuth1")
      : (import.meta.env.VITE_TWITTER_PUBLISHING_OA2_POST_ENDPOINT ?? "/SocialPublishing/TwitterPost");
  }

  public async connect(): Promise<void> {
    await this.connectForPublishing(false);
  }

  private setFreshAuthReady(value: boolean): void {
    window.localStorage.setItem(PublishingTwitterStorage.freshAuthReady, value ? "true" : "false");
  }

  private consumeFreshAuthReady(): boolean {
    const isReady = window.localStorage.getItem(PublishingTwitterStorage.freshAuthReady) === "true";
    if (isReady) {
      window.localStorage.removeItem(PublishingTwitterStorage.freshAuthReady);
    }

    return isReady;
  }

  private setRequiresOAuth1(value: boolean): void {
    window.localStorage.setItem(PublishingTwitterStorage.requiresOAuth1, value ? "true" : "false");
  }

  private getRequiresOAuth1(): boolean {
    return window.localStorage.getItem(PublishingTwitterStorage.requiresOAuth1) === "true";
  }

  private async connectForPublishing(requiresOAuth1: boolean): Promise<void> {
    window.localStorage.setItem(PublishingTwitterStorage.flowMode, "publishing");
    this.setRequiresOAuth1(requiresOAuth1);

    if (this.getOAuthVersion() === "1") {
      await this.openOAuth1Popup();
      return;
    }

    await this.loginOAuth2();
  }

  public async disconnect(): Promise<void> {
    window.localStorage.removeItem(PublishingTwitterStorage.refreshToken);
    window.localStorage.removeItem(PublishingTwitterStorage.accessToken);
    window.localStorage.removeItem(PublishingTwitterStorage.oauth1AccessSecret);
    window.localStorage.removeItem(PublishingTwitterStorage.oauth1AccessToken);
    window.localStorage.removeItem(PublishingTwitterStorage.oauth1ScreenName);
    window.localStorage.removeItem(PublishingTwitterStorage.oauth1UserId);
    window.localStorage.removeItem(PublishingTwitterStorage.codeVerifier);
    window.localStorage.removeItem(PublishingTwitterStorage.oauth1Mode);
    window.localStorage.removeItem(PublishingTwitterStorage.flowMode);
    window.localStorage.removeItem(PublishingTwitterStorage.requiresOAuth1);
    window.localStorage.removeItem(PublishingTwitterStorage.freshAuthReady);
  }

  private checkAuth2State(): boolean {
    const token = window.localStorage.getItem(PublishingTwitterStorage.refreshToken);
    return !!(token && token.length > 0);
  }

  private checkAuth1State(): boolean {
    const token = window.localStorage.getItem(PublishingTwitterStorage.oauth1AccessToken);
    const secret = window.localStorage.getItem(PublishingTwitterStorage.oauth1AccessSecret);
    return !!(token && secret);
  }

  private getTokenDetails(): { refreshToken: string | null; token: string | null; secret: string | null; valid: boolean } {
    const refreshToken = window.localStorage.getItem(PublishingTwitterStorage.refreshToken);
    const token = window.localStorage.getItem(PublishingTwitterStorage.oauth1AccessToken);
    const secret = window.localStorage.getItem(PublishingTwitterStorage.oauth1AccessSecret);
    return {
      refreshToken,
      token,
      secret,
      valid: !!(refreshToken && token && secret)
    };
  }

  private async ensurePublishingLogin(requiresOAuth1: boolean): Promise<boolean> {
    if (this.getOAuthVersion() === "1") {
      if (this.checkAuth1State()) {
        return true;
      }

      try {
        await this.connectForPublishing(true);
        return false;
      } catch {
        return false;
      }
    }

    const token = this.getTokenDetails();

    if (token.refreshToken && (!requiresOAuth1 || (token.token && token.secret))) {
      return true;
    }

    try {
      if (token.refreshToken && requiresOAuth1 && (!token.token || !token.secret)) {
        this.setRequiresOAuth1(true);
        await this.openOAuth1Popup();
      } else {
        await this.connectForPublishing(requiresOAuth1);
      }
      return false;
    } catch {
      return false;
    }
  }

  private async loginOAuth2(): Promise<void> {
    const clientId = this.getOAuth2ClientId();
    const authUrl = this.getOAuth2AuthUrl();

    if (!clientId || !authUrl) {
      throw new Error("Twitter publishing OAuth2 is not configured.");
    }

    const { codeVerifier, codeChallenge } = await this.generatePKCE();
    window.localStorage.setItem(PublishingTwitterStorage.codeVerifier, codeVerifier);
    const redirectUrl = this.getOAuth2RedirectUri();
    const twitterUrl = `${authUrl}?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUrl)}&scope=${this.postingScopes}&state=publishing&code_challenge=${codeChallenge}&code_challenge_method=S256`;
    const popup = window.open(twitterUrl, "_blank", "width=600,height=700");

    if (!popup) {
      throw new Error("The X publishing popup could not be opened.");
    }
  }

  private async requestOAuth1AuthorizationUrl(): Promise<string> {
    window.localStorage.setItem(PublishingTwitterStorage.oauth1Mode, "publishing");
    window.localStorage.setItem(PublishingTwitterStorage.flowMode, "publishing");
    const endpoint = `${this.getOAuth1RequestTokenEndpoint()}?callbackUrl=${encodeURIComponent(this.getOAuth1RedirectUri())}`;
    const response = await this.ajaxService.Get<IApiResponse>(endpoint);

    if (response?.StatusCode !== 200 || !response.ReturnData?.AuthorizationURL) {
      throw new Error("X publishing login could not be started.");
    }

    return response.ReturnData.AuthorizationURL;
  }

  private async openOAuth1Popup(): Promise<void> {
    const authorizationUrl = await this.requestOAuth1AuthorizationUrl();
    const popup = window.open(authorizationUrl, "_blank", "width=600,height=700");
    if (!popup) {
      throw new Error("The X publishing popup could not be opened.");
    }
  }

  private async continueOAuth1InCurrentWindow(): Promise<void> {
    const authorizationUrl = await this.requestOAuth1AuthorizationUrl();
    window.location.href = authorizationUrl;
  }

  public async exchangeOAuth2Token(code: string): Promise<TwitterPublishingExchangeResult> {
    const verifier = window.localStorage.getItem(PublishingTwitterStorage.codeVerifier) ?? "";
    const response = await this.ajaxService.Post<IApiResponse>(this.getOAuth2ExchangeEndpoint(), {
      Code: code,
      RedirectUri: this.getOAuth2RedirectUri(),
      Verifier: verifier
    });

    if (response?.StatusCode === 200 && response.ReturnData) {
      const tokenData = typeof response.ReturnData === "string" ? JSON.parse(response.ReturnData) : response.ReturnData;
      window.localStorage.setItem(PublishingTwitterStorage.refreshToken, tokenData.refresh_token ?? "");
      if (tokenData.access_token) {
        window.localStorage.setItem(PublishingTwitterStorage.accessToken, tokenData.access_token);
      }
      window.localStorage.removeItem(PublishingTwitterStorage.codeVerifier);
      return {
        success: true,
        accessToken: tokenData.access_token ?? null,
        error: null
      };
    }

    window.localStorage.removeItem(PublishingTwitterStorage.codeVerifier);
    return {
      success: false,
      accessToken: null,
      error: response?.Messages?.find(message => typeof message === "string" && message.trim().length > 0)?.trim() ?? "X OAuth2 exchange failed."
    };
  }

  public async exchangeOAuth1Token(token: string, verifier: string): Promise<TwitterPublishingExchangeResult> {
    const response = await this.ajaxService.Post<IApiResponse>(this.getOAuth1ExchangeEndpoint(), {
      RequestToken: token,
      Verifier: verifier
    });

    if (response?.StatusCode === 200 && response.ReturnData) {
      const tokenData = typeof response.ReturnData === "string"
        ? JSON.parse(response.ReturnData)
        : response.ReturnData;

      window.localStorage.setItem(PublishingTwitterStorage.oauth1AccessToken, tokenData.AccessToken ?? "");
      window.localStorage.setItem(PublishingTwitterStorage.oauth1AccessSecret, tokenData.AccessTokenSecret ?? "");
      window.localStorage.setItem(PublishingTwitterStorage.oauth1UserId, tokenData.UserId ?? "");
      window.localStorage.setItem(PublishingTwitterStorage.oauth1ScreenName, tokenData.ScreenName ?? "");
      return {
        success: true,
        accessToken: tokenData.AccessToken ?? null,
        accessTokenSecret: tokenData.AccessTokenSecret ?? null,
        error: null
      };
    }

    return {
      success: false,
      accessToken: null,
      accessTokenSecret: null,
      error: response?.Messages?.find(message => typeof message === "string" && message.trim().length > 0)?.trim() ?? "X OAuth1 exchange failed."
    };
  }

  public async handleCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauth1Token = params.get("oauth_token");
    const oauth1Verifier = params.get("oauth_verifier");

    if (state === "publishing" && code) {
      const oauth2Result = await this.exchangeOAuth2Token(code);
      if (oauth2Result.success) {
        if (this.getRequiresOAuth1()) {
          await this.continueOAuth1InCurrentWindow();
        } else {
          this.setFreshAuthReady(true);
          this.publishConnectResult(true);
        }
        return;
      }

      this.publishConnectResult(false);
      return;
    }

    if (oauth1Token && oauth1Verifier && window.localStorage.getItem(PublishingTwitterStorage.flowMode) === "publishing") {
      const oauth1Result = await this.exchangeOAuth1Token(oauth1Token, oauth1Verifier);
      if (oauth1Result.success) {
        this.setFreshAuthReady(true);
        this.publishConnectResult(true);
      } else {
        this.publishConnectResult(false);
      }
    }
  }

  private publishConnectResult(success: boolean): void {
    if (window.opener) {
      window.opener.postMessage({
        event: EventBus.Event.OnSocialMediaPublishingConnected,
        type: ShareTo.X,
        success
      }, "*");
    }

    window.localStorage.removeItem(PublishingTwitterStorage.flowMode);
    window.localStorage.removeItem(PublishingTwitterStorage.oauth1Mode);
    window.localStorage.removeItem(PublishingTwitterStorage.requiresOAuth1);

    if (window.opener) {
      window.close();
    }
  }

  private async formatMessage(message: string, pageUrl: string, includePageLink: boolean): Promise<string> {
    if (!message) {
      return "";
    }

    let formatted = await PostFormatter.html2Unicode(message);
    if (!includePageLink) {
      return formatted;
    }

    const readMore = "Read More:";
    const maxLength = 257 - 4 - readMore.length;
    if (formatted.length > maxLength) {
      formatted = `${formatted.substring(0, maxLength)}... \n\n${readMore}${pageUrl}`;
    } else {
      formatted += `\n\n${readMore}${pageUrl}`;
    }

    return formatted;
  }

  private async openTweetIntent(sharedItem: IShareSocialMediaItem): Promise<boolean> {
    const text = await PostFormatter.html2Unicode(sharedItem.PostText ?? "");
    const params = new URLSearchParams();

    if (text) {
      params.set("text", text);
    }

    if (sharedItem.IncludePageLink && sharedItem.VersionUrl) {
      params.set("url", sharedItem.VersionUrl);
    }

    const popup = window.open(`${this.intentUrl}?${params.toString()}`, "_blank", "width=700,height=650");
    return !!popup;
  }

  private async postTweet(sharedItem: IShareSocialMediaItem): Promise<boolean> {
    try {
      const token = this.getTokenDetails();
      const message = await this.formatMessage(sharedItem.PostText, sharedItem.VersionUrl, sharedItem.IncludePageLink);
      const command: ITwitterPost = {
        AuthToken: this.getOAuthVersion() === "1" ? "" : (token.refreshToken ?? ""),
        Message: message,
        ImageUrl: sharedItem.ImageUrl,
        UserAccessToken: token.token ?? "",
        UserTokenSecret: token.secret ?? ""
      };

      const response = await this.ajaxService.Post<IApiResponse>(this.getPostEndpoint(), command);

      if (this.getOAuthVersion() !== "1" && response?.ReturnData) {
        window.localStorage.setItem(PublishingTwitterStorage.refreshToken, response.ReturnData);
      } else if (response?.StatusCode !== 200) {
        await this.disconnect();
      }

      return response?.StatusCode === 200;
    } catch (error) {
      await this.disconnect();
      throw error;
    }
  }

  public async sharePost(sharedItem: IShareSocialMediaItem): Promise<ISocialPublishResult> {
    const intentOpened = await this.openTweetIntent(sharedItem);
    return { status: intentOpened ? "success" : "failed" };

    // Keep the API-backed X publishing flow below in place for the future in case
    // platform access changes again and we want to re-enable direct posting.
    const requiresOAuth1 = this.getOAuthVersion() === "1" || !!sharedItem.ImageUrl;
    const hasFreshAuth = this.consumeFreshAuthReady();

    if (!hasFreshAuth) {
      await this.disconnect();
      await this.connectForPublishing(requiresOAuth1);
      return { status: "pending" };
    }

    const isLoggedIn = await this.ensurePublishingLogin(requiresOAuth1);
    const token = this.getTokenDetails();
    const hasRequiredTokens = this.getOAuthVersion() === "1"
      ? !!token.token && !!token.secret
      : !!token.refreshToken && (!requiresOAuth1 || (!!token.token && !!token.secret));

    if (!isLoggedIn || !hasRequiredTokens) {
      return { status: "pending" };
    }

    const posted = await this.postTweet(sharedItem);
    return { status: posted ? "success" : "failed" };
  }

  private async generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
    const generateRandomString = (length: number): string => {
      const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
      let result = "";
      for (let index = 0; index < length; index++) {
        result += charset[Math.floor(Math.random() * charset.length)];
      }
      return result;
    };

    const base64UrlEncode = (buffer: ArrayBuffer): string =>
      btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer))))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");

    const sha256 = async (value: string): Promise<string> => {
      const encoder = new TextEncoder();
      const data = encoder.encode(value);
      const hash = await crypto.subtle.digest("SHA-256", data);
      return base64UrlEncode(hash);
    };

    const codeVerifier = generateRandomString(128);
    const codeChallenge = await sha256(codeVerifier);
    return { codeVerifier, codeChallenge };
  }
}

export default TwitterPublishingProvider;
